export default function TrafficAnalysisSection() {
  return (
    <section id="traffic-analysis" className="relative py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-slate-100 rounded-md border border-slate-200 mb-6">
            <i className="ri-ship-line text-slate-600 text-sm"></i>
            <span className="text-slate-600 text-sm font-medium tracking-wide">交通人流分析</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-black text-slate-900 leading-tight mb-6">
            2025實績與2026推估
            <span className="block mt-2 text-slate-600">以交通旅運量（人次）為基準</span>
          </h2>

          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            金門主要人流來源分為「小三通海運系統」與「國內航空系統」兩大系統。2025年交通旅運量已突破<strong className="text-slate-900 font-black">399萬人次/年</strong>（人次口徑），隨著新碼頭運能提升與翔安機場啟用，2026年將進入更高成長階段。
          </p>

          {/* 2025 Actual Data */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg p-8 text-white mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-md">
                <i className="ri-checkbox-circle-line text-white text-xl"></i>
              </div>
              <div>
                <div className="text-orange-300 text-xs uppercase tracking-wider">2025 實績數據</div>
                <div className="text-white font-bold text-lg">交通旅運量（人次口徑）</div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <div className="text-orange-400 text-sm mb-1">小三通海運</div>
                <div className="text-3xl font-black text-white kpi-range">184萬人次</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <div className="text-blue-400 text-sm mb-1">金門尚義機場</div>
                <div className="text-3xl font-black text-white kpi-range">215萬人次</div>
              </div>
              <div className="bg-gradient-to-br from-orange-500/20 to-blue-500/20 backdrop-blur-sm rounded-lg p-4 border border-orange-400/30">
                <div className="text-orange-300 text-sm mb-1 font-bold">2025 合計</div>
                <div className="text-3xl font-black text-white kpi-range">約399萬人次</div>
              </div>
            </div>
          </div>
        </div>

        {/* B1 區段｜小三通海運（現況營運） */}
        <div className="mb-16">
          <h3 className="text-3xl font-black text-slate-900 mb-8">小三通海運（現況）</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
              <div className="flex items-center space-x-2 mb-3">
                <i className="ri-ship-line text-orange-600 text-xl"></i>
                <div className="text-slate-600 text-sm font-medium">航線</div>
              </div>
              <div className="text-2xl font-black text-slate-900">金門 ↔ 廈門五通</div>
            </div>

            <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
              <div className="flex items-center space-x-2 mb-3">
                <i className="ri-time-line text-orange-600 text-xl"></i>
                <div className="text-slate-600 text-sm font-medium">航運時段</div>
              </div>
              <div className="text-2xl font-black text-slate-900 kpi-range">08:30–17:30</div>
            </div>

            <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
              <div className="flex items-center space-x-2 mb-3">
                <i className="ri-calendar-check-line text-orange-600 text-xl"></i>
                <div className="text-slate-600 text-sm font-medium">每日船班趟數</div>
              </div>
              <div className="text-2xl font-black text-slate-900 kpi-range">16–20 趟</div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6 border-2 border-orange-300">
              <div className="flex items-center space-x-2 mb-3">
                <i className="ri-group-line text-orange-600 text-xl"></i>
                <div className="text-orange-700 text-sm font-medium">人流量（年）</div>
              </div>
              <div className="text-2xl font-black text-orange-600 kpi-range">184萬人次</div>
            </div>
          </div>
        </div>

        {/* B2 區段｜新碼頭（港區擴建後的增量入口） */}
        {/* 已移除此區塊 - 避免重複顯示 */}

        {/* B3 區段｜國內航空（國內線） */}
        <div className="mb-16">
          <h3 className="text-3xl font-black text-slate-900 mb-8">國內航空（國內線）</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
              <div className="flex items-center space-x-2 mb-3">
                <i className="ri-plane-line text-blue-600 text-xl"></i>
                <div className="text-slate-600 text-sm font-medium">航線</div>
              </div>
              <div className="text-xl font-black text-slate-900">金門 ↔ 台北/台中/高雄</div>
            </div>

            <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
              <div className="flex items-center space-x-2 mb-3">
                <i className="ri-flight-takeoff-line text-blue-600 text-xl"></i>
                <div className="text-slate-600 text-sm font-medium">台北航線/日</div>
              </div>
              <div className="text-2xl font-black text-slate-900 kpi-range">14–16 班</div>
            </div>

            <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
              <div className="flex items-center space-x-2 mb-3">
                <i className="ri-calendar-check-line text-blue-600 text-xl"></i>
                <div className="text-slate-600 text-sm font-medium">每日總班次</div>
              </div>
              <div className="text-2xl font-black text-slate-900 kpi-range">18–24 班</div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border-2 border-blue-300">
              <div className="flex items-center space-x-2 mb-3">
                <i className="ri-group-line text-blue-600 text-xl"></i>
                <div className="text-blue-700 text-sm font-medium">人流量（年）</div>
              </div>
              <div className="text-2xl font-black text-blue-600 kpi-range">215萬人次</div>
            </div>
          </div>
        </div>

        {/* C1 區段｜海運＋新碼頭＋航空 統整總覽 */}
        <div className="mb-16">
          <h3 className="text-3xl font-black text-slate-900 mb-8 text-center">整體人流量統整（海運＋新碼頭＋航空）</h3>
          
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg p-10 text-white">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20 text-center">
                <div className="text-orange-400 text-sm mb-2">2025 海運人流量</div>
                <div className="text-3xl font-black text-white kpi-range">184萬人次</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20 text-center">
                <div className="text-blue-400 text-sm mb-2">2025 航空人流量</div>
                <div className="text-3xl font-black text-white kpi-range">215萬人次</div>
              </div>

              <div className="bg-gradient-to-br from-orange-500/30 to-blue-500/30 backdrop-blur-sm rounded-lg p-6 border-2 border-orange-400/50 text-center">
                <div className="text-orange-300 text-sm mb-2 font-bold">2025 合計人流量</div>
                <div className="text-4xl font-black text-white kpi-range">約399萬人次</div>
              </div>

              <div className="bg-gradient-to-br from-green-500/30 to-emerald-500/30 backdrop-blur-sm rounded-lg p-6 border-2 border-green-400/50 text-center">
                <div className="text-green-300 text-sm mb-2 font-bold">新碼頭增量</div>
                <div className="text-4xl font-black text-white kpi-range">+150%</div>
                <div className="text-green-200 text-xs mt-1">承載能力提升</div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-sm rounded-lg p-6 border-2 border-green-400/40 mb-6">
              <div className="text-center">
                <div className="text-green-300 text-sm mb-2 font-bold">2026年預估人流量</div>
                <div className="text-5xl font-black text-white kpi-range mb-2">約598萬人次</div>
                <div className="text-slate-300 text-xs">（399萬 × 150% 成長）</div>
              </div>
            </div>

            <div className="bg-white/5 rounded-lg p-6 border border-white/10">
              <div className="flex items-start space-x-3">
                <i className="ri-information-line text-orange-400 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-slate-300 text-sm leading-relaxed">
                  隨著新碼頭航班量與快速通關，預期國內線航班也增長，<strong className="text-white">2026將進入爆發成長階段</strong>。
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 2026 Scenario Analysis */}
        <div className="mb-12">
          <h3 className="text-2xl font-black text-slate-900 mb-6">2026年人流推估｜三段情境分析</h3>
          
          <div className="space-y-6">
            {/* Base Scenario */}
            <div className="bg-slate-50 rounded-lg p-8 border-2 border-slate-200">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-slate-600 rounded-md flex-shrink-0">
                  <span className="text-white text-xl font-black">Base</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-xl font-black text-slate-900">Base 情境</h4>
                    <span className="px-3 py-1 bg-slate-200 text-slate-700 text-xs font-medium rounded-full whitespace-nowrap">未納入翔安外溢</span>
                  </div>
                  <p className="text-slate-600 text-sm mb-4">僅自然成長與常態調度，不考慮翔安機場外溢效應</p>
                  <div className="text-4xl font-black text-slate-900 kpi-range">420萬–520萬 人次/年</div>
                </div>
              </div>
            </div>

            {/* Upside Scenario */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-8 border-2 border-orange-300">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-500 rounded-md flex-shrink-0">
                  <span className="text-white text-xl font-black">Up</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-xl font-black text-slate-900">Upside 情境</h4>
                    <span className="px-3 py-1 bg-orange-200 text-orange-800 text-xs font-medium rounded-full whitespace-nowrap">新碼頭+翔安機場</span>
                  </div>
                  <p className="text-slate-600 text-sm mb-4">新碼頭運能提升 + 翔安機場年底啟用外溢效應</p>
                  <div className="text-4xl font-black text-orange-600 kpi-range mb-3">520萬–680萬 人次/年</div>
                  <div className="bg-white/60 rounded-lg p-4 border border-orange-200">
                    <div className="flex items-start space-x-2">
                      <i className="ri-information-line text-orange-600 text-lg flex-shrink-0 mt-0.5"></i>
                      <p className="text-slate-700 text-sm">
                        <strong className="text-orange-700">翔安機場外溢效應預估帶來額外 8%–15% 年度旅運增量（情境）</strong>。增量主要反映在平日與非旺季的人流底盤加厚，對餐飲更關鍵。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Aggressive Scenario */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-8 border-2 border-blue-300">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-md flex-shrink-0">
                  <span className="text-white text-xl font-black">Agg</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-xl font-black text-slate-900">Aggressive 情境</h4>
                    <span className="px-3 py-1 bg-blue-200 text-blue-800 text-xs font-medium rounded-full whitespace-nowrap">政策放量/顯著增班</span>
                  </div>
                  <p className="text-slate-600 text-sm mb-4">高成長情境，取決於政策與運能放量</p>
                  <div className="text-4xl font-black text-blue-600 kpi-range mb-3">680萬–800萬+ 人次/年</div>
                  <div className="bg-blue-100 rounded-lg p-3 border border-blue-200">
                    <p className="text-blue-800 text-xs font-medium">
                      ⚠️ 此為高成長情境，取決於政策與運能放量，不作為保證值
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Data Sources */}
        <div className="mt-8 flex flex-wrap justify-center gap-4 text-xs text-slate-500">
          <span>資料來源：</span>
          <a href="https://www.motcmpb.gov.tw/" target="_blank" rel="noopener noreferrer" className="hover:text-orange-600 cursor-pointer whitespace-nowrap">交通部航港局</a>
          <span>|</span>
          <a href="https://www.trip.com/" target="_blank" rel="noopener noreferrer" className="hover:text-orange-600 cursor-pointer whitespace-nowrap">Trip.com</a>
          <span>|</span>
          <a href="https://www.rome2rio.com/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 cursor-pointer whitespace-nowrap">Rome2Rio</a>
          <span>|</span>
          <span className="whitespace-nowrap">金門尚義機場</span>
        </div>
      </div>
    </section>
  );
}
